"use client"

import { useCallback, useEffect, useLayoutEffect, useState } from "react"
import styles from "@styles/TaskCard.module.css"
import { TaskSource } from "./TaskSource"
import { Task } from "@database/tasks/saveTasks"
import { Skeleton } from "@mui/material"
import { sendSlackMessage } from "@/app/helpers/actions/sendSlackReply"
import { useTaskContext } from "@/app/context/TaskContext"
import { SlackThread } from "@/app/helpers/slack/processNewSlackMessages"
import { getSlackThreads } from "@/app/helpers/actions/slack/getSlackThreads"
import { Icon, IconName, IconSize } from "../../icons/Icon"
import clsx from "clsx"
import { EnrichedInternalSlackMessage } from "@/app/helpers/database/slackMessages/getSlackMessages"
import { getEnrichedSlackMessagesAction } from "@/app/helpers/actions/slack/getEnrichedSlackMessages"
import { ToastType, useToast } from "@/app/context/ToastContext"
import { convertDateTimeToTimePassedString } from "@/app/helpers/utils/time"
import { Checkbox } from "../../common/checkbox/Checkbox"
import ChatReplyInput from "../../common/inputs/ChatReplyInput"
import TextArea from "../../common/inputs/TextArea"
import { Button, ButtonVariant } from "../../common/button/Button"
import { Badge } from "../../common/badge/Badge"
import {
  ContextMenuItemType,
  type ContextMenuItemProps,
} from "../../common/contextMenu/ContextMenu"
import TaskCardHeader from "./TaskCardHeader"

export default function SlackTaskCardContent({
  task,
  isFirst,
  isMaximised,
  onMaximisedClose,
}: {
  task: Task
  isFirst: boolean
  isMaximised: boolean
  onMaximisedClose: () => void
}) {
  const [slackMessages, setSlackMessages] = useState<
    EnrichedInternalSlackMessage[]
  >([])
  const [loadingSlackMessages, setLoadingSlackMessages] = useState(false)
  const [sourceSlackMessage, setSourceSlackMessage] =
    useState<EnrichedInternalSlackMessage | null>(null)
  const [sourceSlackThread, setSourceSlackThread] =
    useState<SlackThread | null>(null)
  const [loadingSlackThread, setLoadingSlackThread] = useState(true)
  const [contextMenuOpen, setContextMenuOpen] = useState(false)
  const [hasReplyText, setHasReplyText] = useState(false)

  const { updateTask, deleteTask } = useTaskContext()
  const { showToast } = useToast()

  const fetchSlackMessages = useCallback(async () => {
    setLoadingSlackMessages(true)
    const result = await getEnrichedSlackMessagesAction({
      threadId: task.resourceId,
    })
    if (result.error) {
      console.error("Error fetching Slack messages:", result.error)
      return []
    }
    setSlackMessages(result.messages || [])
    setLoadingSlackMessages(false)
  }, [task.resourceId])

  const fetchSlackThread = useCallback(async () => {
    setLoadingSlackThread(true)
    const result = await getSlackThreads({ threadId: task.resourceId })
    if (result.error) {
      console.error("Error fetching Slack thread:", result.error)
      return null
    }
    setSourceSlackThread(result.threads?.[0] || null)
    setLoadingSlackThread(false)
  }, [task.resourceId])

  // UseEffect hooks

  useEffect(() => {
    fetchSlackMessages()
  }, [fetchSlackMessages])

  useLayoutEffect(() => {
    if (isMaximised) setHasReplyText(false)
  }, [isMaximised])

  useEffect(() => {
    if (slackMessages && slackMessages.length > 0) {
      const newSourceSlackMessage =
        slackMessages.find((message) => message.ts === task.messageId) || null

      if (!newSourceSlackMessage) {
        console.error(
          "No source slack message found for task",
          task.messageId,
          slackMessages,
        )
        return
      }
      // console.log("New source slack message found:", newSourceSlackMessage)
      setSourceSlackMessage(newSourceSlackMessage)
    }
  }, [slackMessages, task.messageId])

  // Helper functions

  const markTaskAsDone = useCallback(() => {
    updateTask({
      taskId: task.taskId,
      completed: true,
    })
  }, [task.taskId, updateTask])

  // Helper variables

  const description =
    sourceSlackThread?.summary || sourceSlackMessage
      ? sourceSlackMessage.text
      : task.description

  const userName = sourceSlackMessage?.sender?.name || null
  const channelName = sourceSlackMessage?.channel?.name
    ? sourceSlackMessage?.channel?.name
    : sourceSlackMessage?.channelId
      ? "Direct message"
      : null

  const replyFormId = "slack-task-reply-form"

  const handleMaximisedReplySubmit = useCallback(
    async (text: string) => {
      if (sourceSlackMessage?.channelId && sourceSlackMessage?.ts && text.trim()) {
        try {
          const result = await sendSlackMessage(
            sourceSlackMessage.channelId,
            sourceSlackMessage.ts,
            text.trim(),
          )
          if (result.error) {
            showToast(
              "Your message couldn't be sent. Please try again.",
              ToastType.Error,
            )
            return text
          }
        } catch (error) {
          showToast(
            "Your message couldn't be sent. Please try again.",
            ToastType.Error,
          )
          return text
        }
      }
      return ""
    },
    [sourceSlackMessage, showToast],
  )

  const handlePostpone = useCallback(() => {
    setContextMenuOpen(false)
    updateTask({ taskId: task.taskId, priority: 1 })
    onMaximisedClose()
  }, [task.taskId, updateTask, onMaximisedClose])

  const handleDelete = useCallback(async () => {
    setContextMenuOpen(false)
    await deleteTask(task.taskId)
    onMaximisedClose()
  }, [task.taskId, deleteTask, onMaximisedClose])

  const maximisedContextMenuItems: ContextMenuItemProps[] = [
    {
      contextMenuItemType: ContextMenuItemType.Option,
      label: "Postpone",
      icon: IconName.ArrowRounded,
      onClick: handlePostpone,
    },
    {
      contextMenuItemType: ContextMenuItemType.Option,
      label: "Delete",
      icon: IconName.TrashCanSimple,
      variant: "destructive",
      onClick: handleDelete,
    },
  ]

  if (isMaximised) {
    return (
      <>
        <TaskCardHeader
          title="Details"
          onClose={onMaximisedClose}
          contextMenuOpen={contextMenuOpen}
          onContextMenuClose={() => setContextMenuOpen(false)}
          contextMenuItems={maximisedContextMenuItems}
          onContextMenuTriggerClick={() => setContextMenuOpen((prev) => !prev)}
          contextMenuPlacement="bottom-left"
        />
        <div className={styles.maximisedContent}>
          <div className={styles.maximisedTaskSection}>
            <div className={styles.maximisedMetaData}>
              <Badge
                label="Slack"
                size="Medium"
                color="Purple"
                filled={false}
              />
              {userName != null && (
                <Badge
                  label={userName}
                  size="Medium"
                  color="Grey"
                  filled={false}
                />
              )}
              {channelName != null && (
                <Badge
                  label={channelName}
                  size="Medium"
                  color="Grey"
                  filled={false}
                />
              )}
            </div>
            <p className={styles.maximisedDescription}>{description}</p>
          </div>
          {sourceSlackMessage && (
            <div className={styles.maximisedReplySection}>
              <TextArea
                formId={replyFormId}
                allowEmptySubmit
                initText=""
                placeholder="Type your answer ..."
                label={`Write answer in ${channelName ?? "this channel"}`}
                disabled={false}
                onSubmit={handleMaximisedReplySubmit}
                onChange={(value) => setHasReplyText(value.trim().length > 0)}
              />
            </div>
          )}
        </div>
        <div className={styles.maximisedFooter}>
          <Button
            type="button"
            label="Send message"
            variant={ButtonVariant.Default}
            disabled={!hasReplyText}
            onClick={() => {
              if (!hasReplyText) return
              const form = document.getElementById(replyFormId) as HTMLFormElement | null
              form?.requestSubmit()
            }}
          />
          <Button
            type="button"
            label="Mark as done"
            variant={ButtonVariant.Filled}
            onClick={() => {
              markTaskAsDone()
              onMaximisedClose()
            }}
          />
        </div>
      </>
    )
  }

  return (
    <>
      <div className={styles.header}>
        <div
          className={styles.headerCheckbox}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox checked={task.completed} onChange={markTaskAsDone} />
        </div>
        <TaskSource source={"slack"} />
      </div>
      <div className={styles.descriptionContainer}>
        <p className={clsx("bodyText", styles.description)}>{description}</p>
      </div>
      <div className={clsx("subHeadline", styles.footer)}>
        {userName ? (
          <span>{userName}</span>
        ) : (
          <Skeleton width={100} height={20} />
        )}
        <div className={styles.footerGroup}>
          <Icon
            name={IconName.IconGroup1}
            size={IconSize.xSmall}
            customColor="#171717"
          />
          {channelName ? (
            <span>{channelName}</span>
          ) : (
            <Skeleton width={100} height={20} />
          )}
        </div>
        <div className={styles.footerGroup}>
          <Icon
            name={IconName.ClockDefault}
            size={IconSize.xSmall}
            customColor="#171717"
          />
          {sourceSlackMessage?.sentAt ? (
            <span>
              {convertDateTimeToTimePassedString(sourceSlackMessage?.sentAt)}
            </span>
          ) : (
            <Skeleton width={30} height={20} />
          )}
        </div>
      </div>
      {sourceSlackMessage && isFirst && (
        <div
          className={styles.replyFieldWrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <ChatReplyInput
            channelId={sourceSlackMessage.channelId || ""}
            threadTs={sourceSlackMessage.ts}
            markTaskAsDone={markTaskAsDone}
          />
        </div>
      )}
    </>
  )
}
